import { GameModes, PlayPositions, PlaySessionTypes } from "@Enums/index";
import { GetUserSessionsResponse } from "@Shared/schemas/sessions/sessionSchemas";
import dayjs from "dayjs";
import { groupBy } from "lodash-es";

export const ACTIVITY_PAGE_SIZE = 50;

interface IUserSessionFields {
  challenge_name?: string | null;
  challenge_title?: string | null;
  name?: string | null;
  title?: string | null;
  play_positions?: PlayPositions[] | null;
  positions?: PlayPositions[] | null;
  selected_positions?: PlayPositions[] | null;
}

export type IUserSession = GetUserSessionsResponse[number] & IUserSessionFields;

export interface IUserSessionGroup {
  month: string;
  items: IUserSession[];
}

interface ISessionModeConfig {
  labelKey: string;
  icon: string;
}

const SESSION_MODES = {
  challenge: {
    labelKey: "challenge.label",
    icon: "https://challenge-thumbs.b-cdn.net/byb-mode-posters/img-challenges-thumb.webp",
  },
  career: {
    labelKey: "home.navigation.career.title",
    icon: "https://challenge-thumbs.b-cdn.net/byb-mode-posters/img-career-thumb.webp",
  },
  matchPlay: {
    labelKey: "home.navigation.matchplay.title",
    icon: "https://challenge-thumbs.b-cdn.net/byb-mode-posters/img-matchplay-thumb.webp",
  },
  practice: {
    labelKey: "title.practice",
    icon: "https://challenge-thumbs.b-cdn.net/byb-mode-posters/img-practice-thumb.webp",
  },
} as const satisfies Record<string, ISessionModeConfig>;

export const getSessionModeConfig = (session: IUserSession) => {
  if (session.session_type === PlaySessionTypes.CHALLENGE_SESSION) {
    return SESSION_MODES.challenge;
  }

  if (session.game_mode === GameModes.MATCH_PLAY) {
    return SESSION_MODES.matchPlay;
  }

  if (session.session_type === PlaySessionTypes.PROGRESSION_SESSION) {
    return SESSION_MODES.career;
  }

  return SESSION_MODES.practice;
};

export const getChallengeTitle = (session: IUserSession) =>
  session.challenge_title || session.challenge_name || session.title || session.name || null;

export const getSessionPositions = (session: IUserSession) => {
  if (Array.isArray(session.selected_positions)) {
    return session.selected_positions.filter(Boolean);
  }

  if (Array.isArray(session.play_positions)) {
    return session.play_positions.filter(Boolean);
  }

  if (Array.isArray(session.positions)) {
    return session.positions.filter(Boolean);
  }

  return session.play_position ? [session.play_position] : [];
};

export const groupSessionsByMonth = (sessions: IUserSession[]): IUserSessionGroup[] => {
  const groupedResults = groupBy(sessions, item => dayjs(item.updated_at).format("MMMM YYYY"));

  return Object.entries(groupedResults).map(([month, items]) => ({
    month,
    items,
  }));
};

export const formatSessionDate = (updatedAt: string) => dayjs(updatedAt).format("MMM D, YYYY");
